<div class="rule-config">
    <!-- Header -->
    <div class="flex justify-content-between align-items-center mb-3">
        <div>
            <h3 class="text-900 font-medium m-0">Neuheits-Regeln</h3>
            <p class="text-500 text-sm mt-1 mb-0">Konfigurieren Sie wie Datenkonflikte aufgelöst werden</p>
        </div>
        <p-button label="Neue Regel"
                  icon="fa-solid fa-plus"
                  (onClick)="openNewRuleDialog()">
        </p-button>
    </div>

    <!-- Rules Table -->
    @if (rules.length > 0) {
        <p-table [value]="rules"
                 styleClass="p-datatable-sm p-datatable-striped"
                 [rowHover]="true">
            <ng-template pTemplate="header">
                <tr>
                    <th style="width: 3rem">Aktiv</th>
                    <th>Name</th>
                    <th>Geltungsbereich</th>
                    <th>Aktion</th>
                    <th style="width: 5rem">Priorität</th>
                    <th style="width: 8rem">Aktionen</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-rule>
                <tr [class.inactive]="!rule.active">
                    <td>
                        <p-inputSwitch [(ngModel)]="rule.active"
                                       (onChange)="toggleRuleActive(rule)">
                        </p-inputSwitch>
                    </td>
                    <td>
                        <span class="font-medium">{{ rule.name }}</span>
                        @if (rule.description) {
                            <br>
                            <span class="text-500 text-sm">{{ rule.description }}</span>
                        }
                    </td>
                    <td>
                        <p-tag [severity]="getScopeSeverity(rule)"
                               [value]="getScopeLabel(rule)">
                        </p-tag>
                    </td>
                    <td>
                        <div class="flex align-items-center gap-2">
                            <i [class]="getActionIcon(rule.action)"></i>
                            <span>{{ getActionLabel(rule.action) }}</span>
                        </div>
                        @if (rule.action === RecencyAction.USE_LOGIC && rule.logicDtoName) {
                            <div class="text-500 text-sm mt-1">
                                <i class="fa-solid fa-code mr-1"></i>
                                {{ rule.logicDtoName }}
                            </div>
                        }
                    </td>
                    <td class="text-center">
                        <p-tag [value]="rule.priority?.toString() || '0'"
                               severity="secondary">
                        </p-tag>
                    </td>
                    <td>
                        <div class="flex gap-1">
                            <p-button icon="fa-solid fa-pencil"
                                      severity="secondary"
                                      [text]="true"
                                      size="small"
                                      pTooltip="Bearbeiten"
                                      (onClick)="editRule(rule)">
                            </p-button>
                            <p-button icon="fa-solid fa-trash"
                                      severity="danger"
                                      [text]="true"
                                      size="small"
                                      pTooltip="Löschen"
                                      (onClick)="deleteRule(rule)">
                            </p-button>
                        </div>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="6" class="text-center text-500 py-4">
                        Keine Regeln definiert. Erstellen Sie eine neue Regel.
                    </td>
                </tr>
            </ng-template>
        </p-table>
    } @else {
        <div class="surface-100 border-round p-4 text-center">
            <i class="fa-solid fa-list-check text-4xl text-500 mb-3"></i>
            <p class="text-500 mb-3">Keine Regeln definiert</p>
            <p-button label="Erste Regel erstellen"
                      icon="fa-solid fa-plus"
                      (onClick)="openNewRuleDialog()">
            </p-button>
        </div>
    }

    <!-- Rule Hierarchy Info -->
    <div class="surface-50 border-round p-3 mt-3">
        <h4 class="text-900 font-medium mt-0 mb-2">
            <i class="fa-solid fa-info-circle mr-2"></i>
            Regel-Hierarchie
        </h4>
        <ul class="m-0 pl-4 text-500 text-sm line-height-3">
            <li><strong>Feld-spezifisch:</strong> Höchste Priorität - gilt nur für ein bestimmtes Feld</li>
            <li><strong>Struktur-spezifisch:</strong> Mittlere Priorität - gilt für alle Felder einer Struktur</li>
            <li><strong>Import Type:</strong> Niedrigste Priorität - Standard für alle Felder</li>
        </ul>
    </div>
</div>

<!-- Rule Dialog -->
<p-dialog [header]="editMode ? 'Regel bearbeiten' : 'Neue Regel'"
          [(visible)]="showRuleDialog"
          [modal]="true"
          [style]="{width: '600px'}">
    <div class="flex flex-column gap-4">
        <!-- Name -->
        <div class="field">
            <label for="rule-name" class="block text-900 font-medium mb-2">Name *</label>
            <input id="rule-name"
                   type="text"
                   pInputText
                   [(ngModel)]="newRule.name"
                   class="w-full"
                   placeholder="z.B. Adressänderungen per Zeitstempel">
        </div>

        <!-- Description -->
        <div class="field">
            <label for="rule-description" class="block text-900 font-medium mb-2">Beschreibung</label>
            <textarea id="rule-description"
                      pInputTextarea
                      [(ngModel)]="newRule.description"
                      rows="2"
                      class="w-full"
                      placeholder="Optionale Beschreibung..."></textarea>
        </div>

        <!-- Scope: Structure -->
        <div class="field">
            <label for="rule-structure" class="block text-900 font-medium mb-2">Struktur (optional)</label>
            <p-select id="rule-structure"
                        [(ngModel)]="newRule.structureDtoId"
                        [options]="structures"
                        optionLabel="name"
                        optionValue="id"
                        [showClear]="true"
                        placeholder="Alle Strukturen"
                        styleClass="w-full">
            </p-select>
            <small class="text-500">Leer lassen für Import Type-weite Regel</small>
        </div>

        <!-- Scope: Field -->
        <div class="field">
            <label for="rule-field" class="block text-900 font-medium mb-2">Feld (optional)</label>
            <p-select id="rule-field"
                        [(ngModel)]="newRule.structureFieldDtoId"
                        [options]="structureFields"
                        optionLabel="attributeName"
                        optionValue="id"
                        [showClear]="true"
                        placeholder="Alle Felder"
                        styleClass="w-full"
                        [filter]="true">
            </p-select>
            <small class="text-500">Leer lassen für Struktur- oder Import Type-weite Regel</small>
        </div>

        <!-- Action -->
        <div class="field">
            <label for="rule-action" class="block text-900 font-medium mb-2">Aktion *</label>
            <p-select id="rule-action"
                        [(ngModel)]="newRule.action"
                        [options]="actionOptions"
                        optionLabel="label"
                        optionValue="value"
                        styleClass="w-full">
            </p-select>
        </div>

        <!-- Logic (conditional) -->
        @if (newRule.action === RecencyAction.USE_LOGIC) {
            <div class="field">
                <label for="rule-logic" class="block text-900 font-medium mb-2">Logik-Baustein *</label>
                <div class="p-inputgroup">
                    <input id="rule-logic"
                           type="number"
                           pInputText
                           [(ngModel)]="newRule.logicDtoId"
                           placeholder="Logic ID eingeben">
                    <span class="p-inputgroup-addon">
                        <i class="fa-solid fa-code"></i>
                    </span>
                </div>
                <small class="text-500">ID des zu verwendenden Logik-Bausteins aus dem LG-Modul</small>
            </div>
        }

        <!-- Priority -->
        <div class="field">
            <label for="rule-priority" class="block text-900 font-medium mb-2">Priorität</label>
            <p-inputNumber id="rule-priority"
                           [(ngModel)]="newRule.priority"
                           [min]="1"
                           [max]="100"
                           styleClass="w-full">
            </p-inputNumber>
            <small class="text-500">Höhere Zahl = wird zuerst geprüft (bei gleicher Spezifität)</small>
        </div>

        <!-- Active -->
        <div class="field flex align-items-center gap-2">
            <p-inputSwitch [(ngModel)]="newRule.active"></p-inputSwitch>
            <label class="text-900">Aktiv</label>
        </div>
    </div>

    <ng-template pTemplate="footer">
        <p-button label="Abbrechen"
                  severity="secondary"
                  (onClick)="cancelDialog()">
        </p-button>
        <p-button [label]="editMode ? 'Speichern' : 'Erstellen'"
                  severity="primary"
                  [disabled]="!newRule.name || newRule.action === undefined"
                  (onClick)="saveRule()">
        </p-button>
    </ng-template>
</p-dialog>
